import * as Sentry from '@sentry/browser';
import { User } from '@sr/types';
import { loadStripe } from '@stripe/stripe-js';
import API from '../../utils/api';
import apiRequest from '../../utils/api-request';
import PRICES, { Price } from './prices';
import { isProduction } from 'utils/env-checks';

export default async function redirectToCheckout(priceId: string, user: User) {
  const price: Price = PRICES[priceId];

  if (!price) {
    throw new Error('Invalid priceId');
  }

  const stripePriceId = isProduction() ? price.productionId : price.stagingId;

  type Response = { sessionId: string };
  const { sessionId }: Response = await apiRequest('stripe/checkout-session', {
    method: 'POST',
    json: {
      price: stripePriceId,
    },
  }).json();

  void API.events.logEvent({
    userId: user.id,
    eventName: 'Checkout Started',
    properties: {
      sku: stripePriceId,
      variant: price.billingPeriod,
      price: price.price,
    },
  });

  try {
    // Get the Stripe publishable key
    const stripePublishableKey = process.env.PUBLIC_STRIPE_PUBLISHABLE_KEY;
    if (!stripePublishableKey) {
      throw new Error('Missing PUBLIC_STRIPE_PUBLISHABLE_KEY');
    }

    // Get the Stripe instance
    const stripe = await loadStripe(stripePublishableKey);

    // Make sure Stripe loaded correctly
    if (stripe === null) {
      throw new Error('Failed to load Stripe instance');
    }

    // Create checkout session
    const session = await stripe.redirectToCheckout({ sessionId });

    if (session.error) {
      throw session.error;
    }
  } catch (error) {
    Sentry.captureException(error, { tags: { noFeedback: true } });
    alert(
      "Our billing provider (Stripe) failed to load. Please assure your ad blocker or privacy extensions aren't blocking it and try again. Email hello@standardresume.co if the problem persists."
    );
  }
}
